// ---------------------------------------------------------------------------
// Metric Thresholds — Shared Types & Generic Classifier
//
// Single source of truth for how numeric metric values are classified into
// named tiers (e.g. DORA's Elite/High/Medium/Low). Consumed by:
//   - frontend/src/lib/dora-utils.ts      (DORA tier badges)
//   - backend/src/services/portfolio-service.ts (DORA response classification)
// A later story (US4) will make these thresholds editable from the DB.
// ---------------------------------------------------------------------------

/**
 * Semantic direction of a metric — determines which bound is "better".
 * - `higher_better`: larger values are better (e.g. deploy frequency, throughput)
 * - `lower_better`: smaller values are better (e.g. MTTR, CFR, cycle time)
 */
export type MetricDirection = 'higher_better' | 'lower_better';

/**
 * A single classification tier within a metric's threshold set.
 *
 * Bounds are open/closed per the `*Inclusive` flags. A value `v` belongs to a
 * tier iff:
 *   (lowerBound === null) OR (lowerBoundInclusive ? v >= lowerBound : v > lowerBound)
 * AND
 *   (upperBound === null) OR (upperBoundInclusive ? v <= upperBound : v < upperBound)
 *
 * Exposing per-tier inclusivity lets us preserve semantics that differ between
 * DORA metrics — e.g. MTTR historically used strict `<` while Lead Time used `<=`.
 */
export interface IMetricTier {
  tierId: string;
  tierOrder: number; // 1 = best tier
  label: string;
  lowerBound: number | null; // null = unbounded below (-∞)
  upperBound: number | null; // null = unbounded above (+∞)
  lowerBoundInclusive: boolean;
  upperBoundInclusive: boolean;
  colorToken: MetricTierColor;
  description: string;
}

export type MetricTierColor = 'success' | 'primary' | 'warning' | 'error';

/** A complete threshold definition for one metric. */
export interface IMetricThresholdSet {
  metricKey: string;
  direction: MetricDirection;
  tiers: IMetricTier[]; // ordered ascending by tierOrder
}

// ---------------------------------------------------------------------------
// Classification
// ---------------------------------------------------------------------------

function matchesTier(value: number, tier: IMetricTier): boolean {
  if (tier.lowerBound !== null) {
    const failsLower = tier.lowerBoundInclusive
      ? value < tier.lowerBound
      : value <= tier.lowerBound;
    if (failsLower) return false;
  }
  if (tier.upperBound !== null) {
    const failsUpper = tier.upperBoundInclusive
      ? value > tier.upperBound
      : value >= tier.upperBound;
    if (failsUpper) return false;
  }
  return true;
}

/**
 * Classify a numeric `value` against the given tier set.
 *
 * Returns the matching `tierId`, or `null` if no tier matches (which should
 * only happen if the tier set is invalid — tiers must cover the full number
 * line for every possible value).
 */
export function classify(value: number, set: IMetricThresholdSet): string | null {
  for (const tier of set.tiers) {
    if (matchesTier(value, tier)) return tier.tierId;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Verify a tier set is well-formed:
 *   - at least one tier
 *   - unique tierId and tierOrder
 *   - tiers cover (-∞, +∞) without gaps
 *   - adjacent tiers do not overlap and have unambiguous boundaries
 *     (exactly one side of a shared boundary is inclusive)
 *
 * Throws `Error` with a descriptive message on any violation.
 */
type TierList = IMetricThresholdSet['tiers'];

/** Reject duplicate tierId / tierOrder within a set. */
function assertUniqueTierKeys(set: IMetricThresholdSet): void {
  const tierIds = new Set<string>();
  const tierOrders = new Set<number>();
  for (const tier of set.tiers) {
    if (tierIds.has(tier.tierId)) {
      throw new Error(`Metric "${set.metricKey}" has duplicate tierId "${tier.tierId}"`);
    }
    if (tierOrders.has(tier.tierOrder)) {
      throw new Error(`Metric "${set.metricKey}" has duplicate tierOrder ${tier.tierOrder}`);
    }
    tierIds.add(tier.tierId);
    tierOrders.add(tier.tierOrder);
  }
}

/** Sort by lowerBound ascending, with null treated as -∞. */
function sortTiersByLowerBound(tiers: TierList): TierList {
  return [...tiers].sort((a, b) => {
    if (a.lowerBound === null && b.lowerBound === null) return 0;
    if (a.lowerBound === null) return -1;
    if (b.lowerBound === null) return 1;
    return a.lowerBound - b.lowerBound;
  });
}

/** The lowest tier must be open below (-∞) and the highest open above (+∞). */
function assertCoversNumberLine(set: IMetricThresholdSet, sorted: TierList): void {
  if (sorted[0].lowerBound !== null) {
    throw new Error(`Metric "${set.metricKey}" does not cover values below ${sorted[0].lowerBound}`);
  }
  const lastTier = sorted.at(-1)!;
  if (lastTier.upperBound !== null) {
    throw new Error(
      `Metric "${set.metricKey}" does not cover values above ${lastTier.upperBound}`,
    );
  }
}

/** Adjacent tiers must touch exactly once with an unambiguous (XOR-inclusive) boundary. */
function assertNoGapsOrOverlaps(set: IMetricThresholdSet, sorted: TierList): void {
  for (let i = 0; i < sorted.length - 1; i++) {
    const current = sorted[i];
    const next = sorted[i + 1];
    if (current.upperBound !== next.lowerBound) {
      throw new Error(
        `Metric "${set.metricKey}" has a gap or overlap between "${current.tierId}" and "${next.tierId}"`,
      );
    }
    if (current.upperBoundInclusive === next.lowerBoundInclusive) {
      throw new Error(
        `Metric "${set.metricKey}" has an ambiguous boundary at ${current.upperBound} between "${current.tierId}" and "${next.tierId}" (both sides ${current.upperBoundInclusive ? 'inclusive' : 'exclusive'})`,
      );
    }
  }
}

export function validateTierSet(set: IMetricThresholdSet): void {
  if (set.tiers.length === 0) {
    throw new Error(`Metric "${set.metricKey}" has no tiers`);
  }
  assertUniqueTierKeys(set);
  const sorted = sortTiersByLowerBound(set.tiers);
  assertCoversNumberLine(set, sorted);
  assertNoGapsOrOverlaps(set, sorted);
}

// ---------------------------------------------------------------------------
// DORA Defaults
//
// These encode the exact boundary semantics of the pre-refactor code in
// frontend/src/lib/dora-utils.ts and backend/src/services/portfolio-service.ts.
// In particular, MTTR uses strict `<` on the upper bound while Lead Time and
// CFR use `<=` — preserved via the `upperBoundInclusive` flag.
// ---------------------------------------------------------------------------

export const DORA_METRIC_KEYS = {
  deployFrequency: 'deployFrequency',
  leadTime: 'leadTime',
  cfr: 'cfr',
  mttr: 'mttr',
} as const;

export type DoraMetricKey = (typeof DORA_METRIC_KEYS)[keyof typeof DORA_METRIC_KEYS];

const DEPLOY_FREQUENCY_THRESHOLDS: IMetricThresholdSet = {
  metricKey: DORA_METRIC_KEYS.deployFrequency,
  direction: 'higher_better',
  tiers: [
    {
      tierId: 'elite',
      tierOrder: 1,
      label: 'Elite',
      lowerBound: 7,
      upperBound: null,
      lowerBoundInclusive: true,
      upperBoundInclusive: false,
      colorToken: 'success',
      description: 'Multiple deploys per day — on-demand',
    },
    {
      tierId: 'high',
      tierOrder: 2,
      label: 'High',
      lowerBound: 1,
      upperBound: 7,
      lowerBoundInclusive: true,
      upperBoundInclusive: false,
      colorToken: 'primary',
      description: 'Between once per day and once per week',
    },
    {
      tierId: 'medium',
      tierOrder: 3,
      label: 'Medium',
      lowerBound: 0.25,
      upperBound: 1,
      lowerBoundInclusive: true,
      upperBoundInclusive: false,
      colorToken: 'warning',
      description: 'Between once per week and once per month',
    },
    {
      tierId: 'low',
      tierOrder: 4,
      label: 'Low',
      lowerBound: null,
      upperBound: 0.25,
      lowerBoundInclusive: false,
      upperBoundInclusive: false,
      colorToken: 'error',
      description: 'Less than once per six months',
    },
  ],
};

const LEAD_TIME_THRESHOLDS: IMetricThresholdSet = {
  metricKey: DORA_METRIC_KEYS.leadTime,
  direction: 'lower_better',
  tiers: [
    {
      tierId: 'elite',
      tierOrder: 1,
      label: 'Elite',
      lowerBound: null,
      upperBound: 1 / 24,
      lowerBoundInclusive: false,
      upperBoundInclusive: true,
      colorToken: 'success',
      description: 'Less than one hour',
    },
    {
      tierId: 'high',
      tierOrder: 2,
      label: 'High',
      lowerBound: 1 / 24,
      upperBound: 7,
      lowerBoundInclusive: false,
      upperBoundInclusive: true,
      colorToken: 'primary',
      description: 'Between one hour and one week',
    },
    {
      tierId: 'medium',
      tierOrder: 3,
      label: 'Medium',
      lowerBound: 7,
      upperBound: 30,
      lowerBoundInclusive: false,
      upperBoundInclusive: true,
      colorToken: 'warning',
      description: 'Between one week and one month',
    },
    {
      tierId: 'low',
      tierOrder: 4,
      label: 'Low',
      lowerBound: 30,
      upperBound: null,
      lowerBoundInclusive: false,
      upperBoundInclusive: false,
      colorToken: 'error',
      description: 'Between one month and six months',
    },
  ],
};

const CFR_THRESHOLDS: IMetricThresholdSet = {
  metricKey: DORA_METRIC_KEYS.cfr,
  direction: 'lower_better',
  tiers: [
    {
      tierId: 'elite',
      tierOrder: 1,
      label: 'Elite',
      lowerBound: null,
      upperBound: 5,
      lowerBoundInclusive: false,
      upperBoundInclusive: true,
      colorToken: 'success',
      description: '0-5% of changes cause a failure',
    },
    {
      tierId: 'high',
      tierOrder: 2,
      label: 'High',
      lowerBound: 5,
      upperBound: 10,
      lowerBoundInclusive: false,
      upperBoundInclusive: true,
      colorToken: 'primary',
      description: '5-10% of changes cause a failure',
    },
    {
      tierId: 'medium',
      tierOrder: 3,
      label: 'Medium',
      lowerBound: 10,
      upperBound: 15,
      lowerBoundInclusive: false,
      upperBoundInclusive: true,
      colorToken: 'warning',
      description: '10-15% of changes cause a failure',
    },
    {
      tierId: 'low',
      tierOrder: 4,
      label: 'Low',
      lowerBound: 15,
      upperBound: null,
      lowerBoundInclusive: false,
      upperBoundInclusive: false,
      colorToken: 'error',
      description: '15-64% of changes cause a failure',
    },
  ],
};

const MTTR_THRESHOLDS: IMetricThresholdSet = {
  metricKey: DORA_METRIC_KEYS.mttr,
  direction: 'lower_better',
  tiers: [
    // Note: MTTR historically uses strict `<` on the upper bound, not `<=`.
    {
      tierId: 'elite',
      tierOrder: 1,
      label: 'Elite',
      lowerBound: null,
      upperBound: 1,
      lowerBoundInclusive: false,
      upperBoundInclusive: false,
      colorToken: 'success',
      description: 'Less than one hour',
    },
    {
      tierId: 'high',
      tierOrder: 2,
      label: 'High',
      lowerBound: 1,
      upperBound: 24,
      lowerBoundInclusive: true,
      upperBoundInclusive: false,
      colorToken: 'primary',
      description: 'Less than 24 hours',
    },
    {
      tierId: 'medium',
      tierOrder: 3,
      label: 'Medium',
      lowerBound: 24,
      upperBound: 168,
      lowerBoundInclusive: true,
      upperBoundInclusive: false,
      colorToken: 'warning',
      description: 'Less than one week',
    },
    {
      tierId: 'low',
      tierOrder: 4,
      label: 'Low',
      lowerBound: 168,
      upperBound: null,
      lowerBoundInclusive: true,
      upperBoundInclusive: false,
      colorToken: 'error',
      description: 'More than one week',
    },
  ],
};

export const DEFAULT_DORA_THRESHOLDS: Readonly<Record<DoraMetricKey, IMetricThresholdSet>> = {
  deployFrequency: DEPLOY_FREQUENCY_THRESHOLDS,
  leadTime: LEAD_TIME_THRESHOLDS,
  cfr: CFR_THRESHOLDS,
  mttr: MTTR_THRESHOLDS,
};
